import { ref, reactive, computed } from '@vue/composition-api'
import { login as loginService, getGoogleOAuthUrl, googleOAuthTokenValidation, getMicrosoftOAuthUrl, microsoftOAuthTokenValidation, logout as logoutService } from '@/app/authentication/Services'
import ServerIsDown from '@/app/shared/exceptions/ServerIsDown'
import webSocketService from '@/services/WebSocketService'
import router from '@/router'
import store from '@/store'
import usePostLoginInit from '@/app/authentication/compositions/UsePostLoginInit'
import useDeviceDetection from '@/app/authentication/compositions/UseDeviceDetection'

export default function useAuthentication () {
  const isLoading = ref(false)
  const loginErrorMessage = ref(null)
  const forceLogoutMessage = ref(null)

  const auth = reactive({
    email: '',
    password: ''
  })

  const isThereErrorLoginMessage = computed(() => {
    return loginErrorMessage.value !== null
  })

  const isThereForceLogoutMessage = computed(() => {
    return forceLogoutMessage.value !== null
  })

  // Initialize post-login composition
  const { performPostLoginInit } = usePostLoginInit(store, router)
  const { isMobile, isDesktop } = useDeviceDetection()

  // ✅ CENTRALIZED LOGIN COMPLETION - SINGLE SOURCE OF TRUTH
  const completeLogin = async (authResponse, loginType = 'normal', deviceInfo = {}) => {
    try {
      // Use provided device info or get from composition scope
      const currentIsMobile = deviceInfo.isMobile || isMobile.value
      const currentIsDesktop = deviceInfo.isDesktop || isDesktop.value

      // Perform complete post-login initialization
      const initResult = await performPostLoginInit(currentIsMobile, currentIsDesktop)

      // Handle routing based on initialization result and device type
      if (initResult.shouldShowMobileWelcome && loginType === 'normal') {
        // Only show mobile welcome for normal login, skip for SSO
        return { shouldShowMobileWelcome: true }
      } else if (authResponse.regions && authResponse.regions.length) {
        // Handle region selection if needed
        router.push({ name: 'regionSelection', params: { regions: authResponse.regions } })
      } else if (initResult.routeTo && currentIsDesktop) {
        // Route to user's default view
        router.push({ name: initResult.routeTo })
      } else {
        // Fallback to Dashboard
        router.push({ name: 'Dashboard' })
      }

      return { success: true, shouldShowMobileWelcome: false }
    } catch (error) {
      console.error('❌ Error during centralized login completion:', error)

      // Fallback routing if initialization fails
      if (authResponse.regions && authResponse.regions.length) {
        router.push({ name: 'regionSelection', params: { regions: authResponse.regions } })
      } else {
        router.push({ name: 'Dashboard' })
      }

      throw error
    }
  }

  // Alias for regular login to match Login.vue expectations
  const login = async (email, password) => {
    try {
      isLoading.value = true
      loginErrorMessage.value = null

      const response = await loginService(email.toLowerCase().trim(), String(password))

      // Store auth token and connect websocket
      localStorage.setItem('accessToken', response.access_token)
      if (response.user_id) {
        webSocketService.connect(response.user_id)
      }

      return response
    } catch (responseError) {
      if (!responseError.status) {
        throw new ServerIsDown('Servidor fuera de servicio')
      } else {
        throw new Error(responseError.data.error)
      }
    } finally {
      isLoading.value = false
    }
  }

  // Legacy method for backwards compatibility
  const performLogin = async (store, router) => {
    try {
      isLoading.value = true
      loginErrorMessage.value = null

      const response = await loginService(auth.email.toLowerCase().trim(), String(auth.password))

      // Store auth token and connect websocket
      localStorage.setItem('accessToken', response.access_token)
      if (response.user_id) {
        webSocketService.connect(response.user_id)
      }

      return response
    } catch (responseError) {
      if (!responseError.status) {
        throw new ServerIsDown('Servidor fuera de servicio')
      } else {
        throw new Error(responseError.data.error)
      }
    } finally {
      isLoading.value = false
    }
  }

  // Google OAuth flow
  const startGoogleLogin = async () => {
    try {
      isLoading.value = true
      loginErrorMessage.value = null

      const response = await getGoogleOAuthUrl()

      if (response && response.auth_url) {
        // Redirect to Google OAuth URL
        window.location.href = response.auth_url
      } else {
        throw new Error('No auth URL received from backend')
      }
    } catch (error) {
      handleLoginError(error)
      isLoading.value = false
    }
  }

  // Handle Google OAuth callback
  const processGoogleCallback = async (authorizationCode) => {
    try {
      const response = await googleOAuthTokenValidation(authorizationCode)

      // Store auth token and connect websocket
      localStorage.setItem('accessToken', response.access_token)
      if (response.user_id) {
        webSocketService.connect(response.user_id)
      }

      return response
    } catch (responseError) {
      // Check if this is a server down error (no status at all)
      if (!responseError.status && !responseError.response?.status && !responseError.data) {
        throw new ServerIsDown('Servidor fuera de servicio')
      } else {
        // Check for specific error cases from /google-oauth endpoint
        const errorData = responseError.data
        const errorMessage = errorData?.error

        // Handle organization error
        if (errorMessage === 'User must belong to an organization') {
          const orgError = new Error('Debes pertenecer a una organización autorizada para acceder. Contacta al administrador del sistema.')
          orgError.response = responseError.response
          orgError.isOrganizationError = true
          throw orgError
        }

        // Handle user not authorized error
        if (errorMessage === 'User not authorized for this system') {
          const notAuthorizedError = new Error('El email no está registrado en Experto Retail')
          notAuthorizedError.response = responseError.response
          notAuthorizedError.isNotAuthorizedError = true
          throw notAuthorizedError
        }

        // For all other errors, throw the original error
        throw responseError
      }
    }
  }

  // Microsoft OAuth flow
  const startMicrosoftLogin = async () => {
    try {
      isLoading.value = true
      loginErrorMessage.value = null

      const response = await getMicrosoftOAuthUrl()

      if (response && response.auth_url) {
        // Redirect to Microsoft OAuth URL
        window.location.href = response.auth_url
      } else {
        throw new Error('No auth URL received from backend')
      }
    } catch (error) {
      handleLoginError(error)
      isLoading.value = false
    }
  }

  // Handle Microsoft OAuth callback
  const processMicrosoftCallback = async (authorizationCode) => {
    try {
      const response = await microsoftOAuthTokenValidation(authorizationCode)

      // Store auth token and connect websocket
      localStorage.setItem('accessToken', response.access_token)
      if (response.user_id) {
        webSocketService.connect(response.user_id)
      }

      return response
    } catch (responseError) {
      // Check if this is a server down error (no status at all)
      if (!responseError.status && !responseError.response?.status && !responseError.data) {
        throw new ServerIsDown('Servidor fuera de servicio')
      } else {
        // Check for specific error cases from /microsoft-oauth endpoint
        const errorData = responseError.data
        const errorMessage = errorData?.error

        // Handle organization error
        if (errorMessage === 'User must belong to an organization') {
          const orgError = new Error('Debes pertenecer a una organización autorizada para acceder. Contacta al administrador del sistema.')
          orgError.response = responseError.response
          orgError.isOrganizationError = true
          throw orgError
        }

        // Handle user not authorized error
        if (errorMessage === 'User not authorized for this system') {
          const notAuthorizedError = new Error('El email no está registrado en Experto Retail')
          notAuthorizedError.response = responseError.response
          notAuthorizedError.isNotAuthorizedError = true
          throw notAuthorizedError
        }

        // For all other errors, throw the original error
        throw responseError
      }
    }
  }

  const handleLoginError = (error) => {
    if (error.name === 'ServerIsDown') {
      loginErrorMessage.value = 'Servidor fuera de servicio'
    } else if (error.message === 'User must belong to an organization' ||
               error.message === 'Debes pertenecer a una organización autorizada para acceder. Contacta al administrador del sistema.') {
      loginErrorMessage.value = 'Debes pertenecer a una organización autorizada para acceder. Contacta al administrador del sistema.'
    } else {
      loginErrorMessage.value = error.message || 'Error al iniciar sesión'
    }
  }

  const logout = async () => {
    try {
      // First, notify the backend to invalidate the session
      await logoutService()
    } catch (error) {
      // Even if backend logout fails, continue with frontend cleanup
      console.warn('⚠️ Backend logout failed, continuing with frontend cleanup:', error.message)
    }

    // Clear frontend authentication state
    localStorage.setItem('accessToken', '')
    webSocketService.disconnect()
  }

  const clearForceLogoutMessage = () => {
    forceLogoutMessage.value = null
  }

  const clearLoginError = () => {
    loginErrorMessage.value = null
  }

  // Check for force logout message from sessionStorage
  const checkForceLogoutMessage = () => {
    const forceLogoutReason = sessionStorage.getItem('force_logout_reason')
    if (forceLogoutReason) {
      forceLogoutMessage.value = forceLogoutReason
      sessionStorage.removeItem('force_logout_reason')
    }
  }

  const isLoggedIn = () => {
    return !!localStorage.getItem('accessToken')
  }

  return {
    // State
    isLoading,
    loginErrorMessage,
    errorMessage: loginErrorMessage, // Alias for Login.vue compatibility
    forceLogoutMessage,
    auth,
    isThereErrorLoginMessage,
    isThereForceLogoutMessage,

    // Methods
    login,
    performLogin,
    startGoogleLogin,
    processGoogleCallback,
    startMicrosoftLogin,
    processMicrosoftCallback,
    handleLoginError,
    logout,
    clearForceLogoutMessage,
    clearLoginError,
    checkForceLogoutMessage,
    isLoggedIn,

    // Centralized completion method
    completeLogin
  }
}



// WEBPACK FOOTER //
// ./src/app/authentication/compositions/UseAuthentication.js